import type { AnnouncementKind, PortalNotification } from '@central/api';
import { StatusChip, color, formatDate, status, type StatusTone } from '@central/ui';
import { Button, Modal, Popover, UnstyledButton } from '@mantine/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useRouteContext } from '@tanstack/react-router';
import { useEffect, useId, useState } from 'react';
import { api, notificationsQuery } from '../api';
import { ComposeButton } from '../components/ComposeButton';

const kindChip: Record<AnnouncementKind, { tone: StatusTone; label: string }> = {
  release: { tone: 'blu', label: 'New release' },
  maintenance: { tone: 'warn', label: 'Maintenance' },
  notice: { tone: 'unk', label: 'Notice' },
  security: { tone: 'warn', label: 'Security' },
};

/** Plain-text body split on blank lines. Rendered as text nodes, never as HTML. */
function paragraphs(body: string): string[] {
  return body
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);
}

/**
 * Announcements for the signed-in user (docs/design/CustomerAnnounce.dc.html):
 * banners and the bell above every page, and the "What's new" pop-up once
 * per sign-in for pop-up items not yet seen. A failed load shows nothing.
 */
export function Notifications() {
  const { me } = useRouteContext({ from: '/app' });
  const queryClient = useQueryClient();
  const { data } = useQuery(notificationsQuery);
  const items = data?.items ?? [];
  // The pop-up's items are fixed when the list first arrives, so marking
  // them seen does not change what is on screen.
  const [popup, setPopup] = useState<PortalNotification[]>();
  const [bellOpen, setBellOpen] = useState(false);
  const bellHeading = useId();

  useEffect(() => {
    if (data && popup === undefined)
      setPopup(data.items.filter((n) => n.channels.popup && !n.seenAt));
  }, [data, popup]);

  const markSeen = async (ids: string[]) => {
    if (ids.length === 0) return;
    await Promise.allSettled(ids.map((id) => api.notifications.seen(id)));
    await queryClient.invalidateQueries({ queryKey: notificationsQuery.queryKey });
  };

  const bell = items.filter((n) => n.channels.bell);
  const unread = bell.filter((n) => !n.seenAt);

  // Opening the bell marks what it lists as seen.
  const openBell = (extra: string[] = []) => {
    setBellOpen(true);
    void markSeen([...new Set([...extra, ...unread.map((n) => n.id)])]);
  };

  const closePopup = (thenBell = false) => {
    const ids = (popup ?? []).map((n) => n.id);
    setPopup([]);
    if (thenBell) openBell(ids);
    else void markSeen(ids);
  };
  const banners = items.filter((n) => n.channels.banner);

  return (
    <>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {banners.map((n) => (
            <Banner key={n.id} n={n} />
          ))}
        </div>
        <Popover
          opened={bellOpen}
          onChange={setBellOpen}
          position="bottom-end"
          width={380}
          shadow="md"
        >
          <Popover.Target>
            <UnstyledButton
              aria-label={`Notifications, ${unread.length || 'none'} unread`}
              onClick={() => (bellOpen ? setBellOpen(false) : openBell())}
              style={{
                position: 'relative',
                width: 44,
                height: 44,
                flex: 'none',
                borderRadius: 10,
                border: `1px solid ${color.borderStrong}`,
                background: color.surface,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <BellIcon />
              {unread.length > 0 && (
                <span
                  aria-hidden
                  style={{
                    position: 'absolute',
                    top: -6,
                    right: -6,
                    minWidth: 20,
                    height: 20,
                    padding: '0 5px',
                    boxSizing: 'border-box',
                    borderRadius: 10,
                    background: color.badge,
                    color: color.surface,
                    fontSize: 12,
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {unread.length}
                </span>
              )}
            </UnstyledButton>
          </Popover.Target>
          <Popover.Dropdown role="dialog" aria-labelledby={bellHeading} p={0}>
            <h2
              id={bellHeading}
              style={{
                margin: 0,
                padding: '14px 16px',
                fontSize: 15,
                borderBottom: `1px solid ${color.rowRule}`,
              }}
            >
              Notifications
            </h2>
            <div style={{ maxHeight: 480, overflowY: 'auto' }}>
              {bell.length === 0 ? (
                <p style={{ margin: 0, padding: 16, fontSize: 14, color: color.textMuted }}>
                  Nothing new.
                </p>
              ) : (
                bell.map((n, i) => (
                  <div
                    key={n.id}
                    style={{
                      padding: '14px 16px',
                      borderTop: i ? `1px solid ${color.rowRule}` : undefined,
                    }}
                  >
                    <Item n={n} compact onAction={() => setBellOpen(false)} />
                  </div>
                ))
              )}
            </div>
          </Popover.Dropdown>
        </Popover>
      </div>
      <Modal.Root opened={!!popup?.length} onClose={() => closePopup()} size={560} yOffset={120}>
        <Modal.Overlay color={color.text} backgroundOpacity={0.55} />
        <Modal.Content radius={12}>
          <Modal.Header
            style={{
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 4,
              padding: '28px 28px 0',
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 600, color: color.textMuted }}>
              Since you last signed in
              {me.user.previousSignInAt && ` · ${formatDate(me.user.previousSignInAt)}`}
            </span>
            <Modal.Title
              component="h2"
              style={{ margin: 0, fontSize: 24, lineHeight: '32px', fontWeight: 700 }}
            >
              What’s new
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ padding: 28, display: 'flex', flexDirection: 'column', gap: 20 }}>
            {(popup ?? []).map((n, i) => (
              <article
                key={n.id}
                style={{
                  paddingTop: i ? 18 : 0,
                  borderTop: i ? `1px solid ${color.rowRule}` : undefined,
                }}
              >
                <Item n={n} onAction={() => closePopup()} />
              </article>
            ))}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 12,
                flexWrap: 'wrap',
              }}
            >
              <Button variant="subtle" px={0} onClick={() => closePopup(true)}>
                See all announcements
              </Button>
              <Button h={44} onClick={() => closePopup()}>
                Got it
              </Button>
            </div>
          </Modal.Body>
        </Modal.Content>
      </Modal.Root>
    </>
  );
}

/**
 * A release item's links: its notes (the Releases page when the release
 * has no notes link) and a prefilled message to schedule the upgrade.
 * onAction runs on either, to close what the item is shown in.
 */
function ReleaseActions({ n, onAction }: { n: PortalNotification; onAction?: () => void }) {
  const version = n.releaseVersion;
  if (!version) return null;
  return (
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }} onClick={onAction}>
      {n.releaseNotesUrl ? (
        <Button
          component="a"
          href={n.releaseNotesUrl}
          target="_blank"
          rel="noreferrer"
          variant="default"
          h={40}
        >
          Release notes
        </Button>
      ) : (
        <Button component={Link} to="/releases" variant="default" h={40}>
          Release notes
        </Button>
      )}
      <ComposeButton kind="question" subject={`Schedule an upgrade to ${version}`}>
        Schedule upgrade
      </ComposeButton>
    </div>
  );
}

function Item({
  n,
  compact = false,
  onAction,
}: {
  n: PortalNotification;
  compact?: boolean;
  onAction?: () => void;
}) {
  const chip = kindChip[n.kind];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: compact ? 6 : 8 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <StatusChip tone={chip.tone}>{chip.label}</StatusChip>
        <span style={{ fontSize: 13, color: color.textMuted }}>{formatDate(n.publishAt)}</span>
      </div>
      <h3 style={{ margin: 0, fontSize: compact ? 15 : 18 }}>{n.title}</h3>
      {paragraphs(n.body).map((p, i) => (
        <p
          key={i}
          style={{
            margin: 0,
            fontSize: compact ? 14 : 15,
            lineHeight: compact ? '21px' : '23px',
            color: color.textBody,
            whiteSpace: 'pre-line',
          }}
        >
          {p}
        </p>
      ))}
      {n.kind === 'release' && <ReleaseActions n={n} onAction={onAction} />}
    </div>
  );
}

function Banner({ n }: { n: PortalNotification }) {
  const tone = status[kindChip[n.kind].tone];
  const first = paragraphs(n.body)[0];
  return (
    <div
      role="region"
      aria-label="Announcement"
      style={{
        background: tone.bg,
        color: tone.fg,
        borderRadius: 8,
        padding: '10px 14px',
        fontSize: 14,
        lineHeight: '20px',
      }}
    >
      <strong>{n.title}</strong>
      {first && <span> · {first}</span>}
    </div>
  );
}

function BellIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      style={{ color: color.text }}
    >
      <path d="M6 8a6 6 0 1 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.9 1.9 0 0 0 3.4 0" />
    </svg>
  );
}
